import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Globe, GraduationCap, Briefcase } from 'lucide-react';

import { getEmployeeResume } from '../../../services/resumeService';
import { formatDate } from '../../../utils/Utils';

export default function EmployeeResumeTab({ employeeId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEmployeeResume(employeeId)
      .then((res) => setResume(res.data?.data))
      .catch(() => setResume(null))
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <LoadingText>{t('common.loading') || '...'}</LoadingText>;

  const languages = resume?.languages || [];
  const educations = resume?.educations || [];
  const workExperiences = resume?.workExperiences || [];

  return (
    <Container>
      <SectionCard>
        <SectionHeader>
          <Globe size={18} />
          <SectionTitle>{t('resume.languages') || 'Languages'}</SectionTitle>
        </SectionHeader>
        {languages.length === 0 ? (
          <EmptyText>{t('common.noRecords') || '-'}</EmptyText>
        ) : (
          <ItemList>
            {languages.map((lang) => (
              <LanguageItem key={lang.id}>
                <LanguageName>
                  {lang.name || '-'}
                  {lang.isNative && (
                    <NativeBadge>{t('resume.native') || 'Native'}</NativeBadge>
                  )}
                </LanguageName>
                {!lang.isNative && (
                  <LanguageLevels>
                    <LevelTag>{t('resume.speaking') || 'Speaking'}: {lang.speakingLevel || '-'}</LevelTag>
                    <LevelTag>{t('resume.writing') || 'Writing'}: {lang.writingLevel || '-'}</LevelTag>
                  </LanguageLevels>
                )}
              </LanguageItem>
            ))}
          </ItemList>
        )}
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <GraduationCap size={18} />
          <SectionTitle>{t('resume.educations') || 'Education'}</SectionTitle>
        </SectionHeader>
        {educations.length === 0 ? (
          <EmptyText>{t('common.noRecords') || '-'}</EmptyText>
        ) : (
          <ItemList>
            {educations.map((edu) => (
              <EducationItem key={edu.id}>
                <EduHeader>
                  <EduTitle>
                    {edu.level && <EduLevel>{edu.level}</EduLevel>}
                    {edu.faculty || '-'}
                  </EduTitle>
                </EduHeader>
                <EduInstitution>{edu.institution || '-'}</EduInstitution>
                <EduMeta>
                  {edu.status && <MetaTag>{edu.status}</MetaTag>}
                  {edu.graduationDate && (
                    <MetaText>{formatDate(edu.graduationDate, lang)}</MetaText>
                  )}
                </EduMeta>
              </EducationItem>
            ))}
          </ItemList>
        )}
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <Briefcase size={18} />
          <SectionTitle>{t('resume.workExperiences') || 'Work experience'}</SectionTitle>
        </SectionHeader>
        {workExperiences.length === 0 ? (
          <EmptyText>{t('common.noRecords') || '-'}</EmptyText>
        ) : (
          <ItemList>
            {workExperiences.map((exp) => (
              <WorkItem key={exp.id}>
                <WorkTitle>{exp.title || '-'}</WorkTitle>
                <WorkCompany>{exp.company || '-'}</WorkCompany>
                <WorkMeta>
                  {exp.startDate && (
                    <MetaText>
                      {formatDate(exp.startDate, lang)}
                      {exp.endDate ? ` - ${formatDate(exp.endDate, lang)}` : ` - ${t('employee.continuingPosition')}`}
                    </MetaText>
                  )}
                  {exp.location && <MetaText>{exp.location}</MetaText>}
                </WorkMeta>
              </WorkItem>
            ))}
          </ItemList>
        )}
      </SectionCard>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoadingText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  padding: 40px;
  text-align: center;
`;

const EmptyText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #A0A8B4;
`;

const SectionCard = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #E8ECF0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/* Languages */
const LanguageItem = styled.div`
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.bgColor || '#F5F7FA'};
  border-radius: 6px;
`;

const LanguageName = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const NativeBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: #dcfce7;
  color: #16a34a;
`;

const LanguageLevels = styled.div`
  display: flex;
  gap: 12px;
`;

const LevelTag = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748b;
`;

/* Education */
const EducationItem = styled.div`
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.bgColor || '#F5F7FA'};
  border-radius: 6px;
`;

const EduHeader = styled.div`
  margin-bottom: 4px;
`;

const EduTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EduLevel = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: #dbeafe;
  color: #2563eb;
`;

const EduInstitution = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
`;

const EduMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MetaTag = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #64748b;
`;

const MetaText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #94a3b8;
`;

/* Work Experience */
const WorkItem = styled.div`
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.bgColor || '#F5F7FA'};
  border-radius: 6px;
`;

const WorkTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
`;

const WorkCompany = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
`;

const WorkMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;
